import { FC, useMemo, SetStateAction } from 'react';
import { FilterField } from './types';
import useIsDirty, { isFieldDirty } from './hooks/use-dirty.hook';
import { useResetFilters } from './hooks';
import { FilterFieldValue } from './components';
import { FilterResult, FilterResultBlock } from '../custom-filter';

type Props = {
  filters: Record<string, any>;
  fields: FilterField[];
  results: number;
  onChange: (value: SetStateAction<any>) => void;
  onResetFilters?: VoidFunction; // Optional custom reset function
  canReset?: boolean; // Optional custom canReset logic
};

export const FilterHubResult: FC<Props> = ({
  fields,
  filters,
  onChange,
  results,
  onResetFilters,
  canReset,
}) => {
  const fieldsNames = useMemo(() => fields.map((field) => field.name), [fields]);
  const isDirty = useIsDirty(fieldsNames, filters);
  const defaultHandleReset = useResetFilters({ filters, fields, onChange });

  // Use custom reset function if provided, otherwise use default
  const handleReset = onResetFilters || defaultHandleReset;

  // Use custom canReset if provided, otherwise use isDirty
  const shouldShowReset = canReset !== undefined ? canReset : isDirty;

  return (
    <FilterResult canReset={shouldShowReset} onResetFilters={handleReset}>
      {fields.map((field) => {
        if (!isFieldDirty(field.name, filters)) return null;

        if (field.type === 'checkbox' && !filters[field.name]) return null;

        // Always wrap each field in FilterResultBlock for consistent categorization
        return (
          <FilterResultBlock key={field.name} label={field.label}>
            <FilterFieldValue onRemove={onChange} field={field} filters={filters} />
          </FilterResultBlock>
        );
      })}
    </FilterResult>
  );
};
